import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFire, 
  faChartLine, 
  faLightbulb,
  faExclamationTriangle,
  faCheckCircle,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { burnoutAPI } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Burnout = () => {
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState({
    workload: 5,
    support: 5,
    autonomy: 5,
    recognition: 5,
    workLifeBalance: 5,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSliderChange = (field, value) => {
    setAssessment({ ...assessment, [field]: parseInt(value) });
  };

  const calculateRisk = () => {
    const scores = Object.values(assessment);
    const average = scores.reduce((a, b) => a + b, 0) / scores.length;
    
    if (average <= 3) {
      return { level: 'High Risk', color: '#ef4444', message: 'You may be experiencing significant burnout. Consider seeking support.' };
    } else if (average <= 5) {
      return { level: 'Moderate Risk', color: '#f59e0b', message: 'Some signs of burnout detected. Take time for self-care.' };
    } else if (average <= 7) {
      return { level: 'Low Risk', color: '#10b981', message: 'You\'re managing well! Keep maintaining healthy boundaries.' };
    } else {
      return { level: 'Minimal Risk', color: '#22c55e', message: 'Excellent! You have a healthy work-life balance.' };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Try to submit to backend
      await burnoutAPI.assess(assessment);
      const risk = calculateRisk();
      setResult(risk);
      toast.success('Assessment completed!');
    } catch (error) {
      // If backend not available, calculate locally
      const risk = calculateRisk();
      setResult(risk);
      toast.success('Assessment completed!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '2rem'
    }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard')} 
          style={{
            background: 'none',
            border: '1px solid #FFB6C1',
            color: '#FFB6C1',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '2rem'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>

        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
            <FontAwesomeIcon icon={faFire} /> Burnout Assessment
          </h1>
          <p style={{ color: '#999' }}>
            Rate the following aspects of your work life (1 = Very Poor, 10 = Excellent)
          </p>
        </div>

        {/* Assessment Form */}
        <form onSubmit={handleSubmit}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem'
          }}>
            {/* Workload */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#FFB6C1', fontWeight: '600' }}>
                Workload Management
              </label>
              <p style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1rem' }}>
                How manageable is your current workload?
              </p>
              <input
                type="range"
                min="1"
                max="10"
                value={assessment.workload}
                onChange={(e) => handleSliderChange('workload', e.target.value)}
                style={{ width: '100%', accentColor: '#FFB6C1' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                <span>Overwhelming</span>
                <span style={{ color: '#FFB6C1', fontWeight: '600' }}>{assessment.workload}</span>
                <span>Very Manageable</span>
              </div>
            </div>

            {/* Support */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#FFB6C1', fontWeight: '600' }}>
                Team Support
              </label>
              <p style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1rem' }}>
                How supported do you feel by your team/manager?
              </p>
              <input
                type="range"
                min="1"
                max="10"
                value={assessment.support}
                onChange={(e) => handleSliderChange('support', e.target.value)}
                style={{ width: '100%', accentColor: '#FFB6C1' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                <span>No Support</span>
                <span style={{ color: '#FFB6C1', fontWeight: '600' }}>{assessment.support}</span>
                <span>Fully Supported</span>
              </div>
            </div>

            {/* Autonomy */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#FFB6C1', fontWeight: '600' }}>
                Work Autonomy
              </label>
              <p style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1rem' }}>
                How much control do you have over your work decisions?
              </p>
              <input
                type="range"
                min="1"
                max="10"
                value={assessment.autonomy}
                onChange={(e) => handleSliderChange('autonomy', e.target.value)}
                style={{ width: '100%', accentColor: '#FFB6C1' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                <span>No Control</span>
                <span style={{ color: '#FFB6C1', fontWeight: '600' }}>{assessment.autonomy}</span>
                <span>Full Autonomy</span>
              </div>
            </div>

            {/* Recognition */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#FFB6C1', fontWeight: '600' }}>
                Recognition & Appreciation
              </label>
              <p style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1rem' }}>
                How valued do you feel for your contributions?
              </p>
              <input
                type="range"
                min="1"
                max="10"
                value={assessment.recognition}
                onChange={(e) => handleSliderChange('recognition', e.target.value)}
                style={{ width: '100%', accentColor: '#FFB6C1' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                <span>Not Valued</span>
                <span style={{ color: '#FFB6C1', fontWeight: '600' }}>{assessment.recognition}</span>
                <span>Highly Valued</span>
              </div>
            </div>

            {/* Work-Life Balance */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#FFB6C1', fontWeight: '600' }}>
                Work-Life Balance
              </label>
              <p style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1rem' }}>
                How well are you balancing work and personal life?
              </p>
              <input
                type="range"
                min="1"
                max="10"
                value={assessment.workLifeBalance}
                onChange={(e) => handleSliderChange('workLifeBalance', e.target.value)}
                style={{ width: '100%', accentColor: '#FFB6C1' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                <span>Very Poor</span>
                <span style={{ color: '#FFB6C1', fontWeight: '600' }}>{assessment.workLifeBalance}</span>
                <span>Excellent</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: '#FFB6C1',
                border: 'none',
                borderRadius: '8px',
                color: '#000',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Calculating...' : 'Calculate Burnout Risk'}
            </button>
          </div>
        </form>

        {/* Results */}
        {result && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: `2px solid ${result.color}`,
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <FontAwesomeIcon 
                icon={result.level.includes('High') ? faExclamationTriangle : faCheckCircle} 
                style={{ fontSize: '3rem', color: result.color, marginBottom: '1rem' }}
              />
              <h2 style={{ color: result.color, fontSize: '1.8rem', marginBottom: '0.5rem' }}>
                {result.level}
              </h2>
              <p style={{ color: '#999', fontSize: '1.1rem' }}>
                {result.message}
              </p>
            </div>

            <div style={{ borderTop: '1px solid #333', paddingTop: '1.5rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#FFB6C1' }}>
                <FontAwesomeIcon icon={faLightbulb} /> Recommendations
              </h3>
              <ul style={{ color: '#fff', paddingLeft: '1.5rem' }}>
                <li style={{ marginBottom: '0.5rem' }}>Take regular breaks throughout your workday</li>
                <li style={{ marginBottom: '0.5rem' }}>Set clear boundaries between work and personal time</li>
                <li style={{ marginBottom: '0.5rem' }}>Communicate openly with your manager about workload</li>
                <li style={{ marginBottom: '0.5rem' }}>Practice stress-reduction techniques (meditation, exercise)</li>
                <li style={{ marginBottom: '0.5rem' }}>Connect with colleagues and build support networks</li>
              </ul>
            </div>
          </div>
        )}

        {/* Info Card */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>About Burnout</h3>
          <p style={{ color: '#999', lineHeight: '1.6' }}>
            Burnout is a state of emotional, physical, and mental exhaustion caused by prolonged stress. 
            Regular self-assessment helps you stay aware of your wellbeing and take proactive steps to 
            maintain a healthy work-life balance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Burnout;
